/**
 * Session Tracking Context
 *
 * Provides session tracking functionality across the app.
 * Part of Phase 3 of the Lomi Context Framework.
 *
 * Note: This context depends on AuthContext being available (parent provider).
 * Session is automatically started when user becomes authenticated.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import useSessionTracker from '../hooks/useSessionTracker';

interface SessionContextType {
  sessionId: string | null;
  isActive: boolean;
  trackScreenView: (screen: string, metadata?: Record<string, any>) => void;
  trackComponentView: (componentId: string, componentType: string, moduleId?: string, order?: number) => void;
  trackComponentExit: (componentId: string, componentType: string, moduleId?: string) => void;
  trackAnswerSubmit: (componentId: string, isCorrect: boolean, metadata?: Record<string, any>) => void;
  trackHelpRequest: (question: string, context?: Record<string, any>) => void;
  trackModuleStart: (moduleId: string) => void;
  trackModuleComplete: (moduleId: string) => void;
  flushEvents: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function SessionProvider({ children, enabled = true }: SessionProviderProps) {
  // Get auth state to trigger session start on login
  const { isAuthenticated } = useAuth();

  // Pass isAuthenticated to trigger session start when user logs in
  const sessionTracker = useSessionTracker({ enabled, isAuthenticated });

  return (
    <SessionContext.Provider value={sessionTracker}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

/**
 * HOC to wrap a component with session tracking for its component view
 */
export function withSessionTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentId: string,
  componentType: string
) {
  return function SessionTrackedComponent(props: P) {
    const { trackComponentView, trackComponentExit } = useSession();

    React.useEffect(() => {
      trackComponentView(componentId, componentType);
      return () => {
        trackComponentExit(componentId, componentType);
      };
    }, [trackComponentView, trackComponentExit]);

    return <WrappedComponent {...props} />;
  };
}

export default SessionContext;
